import { useEffect, useState } from 'react';
import Highcharts from 'highcharts';
import { useDispatch, useSelector } from 'react-redux';
import { bindActionCreators } from 'redux';
import { useAnalytics, useAuthentication } from '@ecosystem/newron-client-lib';
import { useSubscriptions } from '@components/subscriptions/SubscriptionsProvider';
import { usePageConfig } from '@utils/pageConfig/PagesConfigContext';
import { setLocalStorage, StorageKey, validateLocalStorageByUserData } from '@utils/StorageUtils';
import { getNewronLogoutPath } from '@utils/UrlUtils';
import {
  checkIsSameUser,
  getAndStoreSavedFilters,
  getFiltersAndRetailerClients,
  validateSubscriptionAndGetRetailerInfo,
} from '@utils/AppSubscriptionUtils';
import { getSubscriptionWithProductType } from '@utils/SubsciptionsUtils';
import { isAuthorized } from '@utils/Permission';
import { ErrorTagEnum, saveErrorLog } from '@utils/LogUtil';
import {
  actionCreatorsHitlistsRetailerClient,
  actionCreatorsInitialFilters,
  actionCreatorsInitialFiltersMarketPremium,
  actionCreatorsInitialFiltersProducts,
  actionCreatorsMarketPremiumClientState,
  actionCreatorsRetailerClient,
  actionCreatorsSellerInsightsRetailerClient,
  actionCreatorsSideFilters,
  actionCreatorsSubscribedProductTypes,
} from '@state/Index';
import { ProductDataType, ProductType } from '@pages/product-analyzer/Product';
import { PageName } from '@utils/pageConfig/Types';
import { RetailerClient } from '@models/RetailerClient';

export function logoutUser(event: StorageEvent, isAuthenticated: boolean) {
  if (validateLocalStorageByUserData(event.key, isAuthenticated)) {
    window.location.href = getNewronLogoutPath();
  }
}

export function logSubscriptionError(err: any, productType: ProductType, setLoading: (v: boolean) => void) {
  setLoading(false);
  saveErrorLog(err, `App - Get ${productType} client subscription error.`, {
    category: ErrorTagEnum.ApiError,
    requestPayload: '',
  });
}

interface FetchSectionOptions {
  pageName: PageName;
  productType: ProductType;
  productDataType: ProductDataType;
  setSubscription: (sub: any) => void;
  reduxState: any;
  updateRedux: (...args: any[]) => void;
  setId: (id: number) => void;
  setCountries: (c: any[]) => void;
  setList: (v: RetailerClient[]) => void;
  setLoadingFn: (v: boolean) => void;
}

export function useInitializeSubscriptions({ setLoading }: { setLoading: (v: boolean) => void }) {
  const dispatch = useDispatch();
  const { page } = useAnalytics(); //NOSONAR typescript:S1874
  const { isAuthenticated, user } = useAuthentication();
  const { subscriptions, loadingSubscriptions } = useSubscriptions();
  const pagesConfig = usePageConfig();

  const reduxStates = {
    retailer: useSelector((s: any) => s.retailerClient),
    hitlists: useSelector((s: any) => s.hitlistsRetailerClient),
    market: useSelector((s: any) => s.marketPremiumRetailerClient),
    seller: useSelector((s: any) => s.sellerInsightsRetailerClient),
  };

  const { addSubscribedProductType, clearSubscribedProductType } = bindActionCreators(
    actionCreatorsSubscribedProductTypes,
    dispatch,
  );

  const {
    updateRetailerClient,
    updateHitlistsRetailerClient,
    updateMarketPremiumRetailerClient,
    updateSellerInsightsRetailerClient,
  } = bindActionCreators(
    {
      ...actionCreatorsRetailerClient,
      ...actionCreatorsHitlistsRetailerClient,
      ...actionCreatorsMarketPremiumClientState,
      ...actionCreatorsSellerInsightsRetailerClient,
    },
    dispatch,
  );

  const { updateInitialFilters, updateInitialFiltersProducts, updateInitialFiltersMarketPremium, updateSideFilters } =
    bindActionCreators(
      {
        ...actionCreatorsInitialFilters,
        ...actionCreatorsInitialFiltersProducts,
        ...actionCreatorsInitialFiltersMarketPremium,
        ...actionCreatorsSideFilters,
      },
      dispatch,
    );

  const [retailer, setRetailer] = useState<RetailerClient[]>([]);
  const [hitlists, setHitlists] = useState<RetailerClient[]>([]);
  const [retailKad, setRetailKad] = useState<RetailerClient[]>([]);
  const [market, setMarket] = useState<RetailerClient[]>([]);
  const [seller, setSeller] = useState<RetailerClient[]>([]);

  const [hitlistsLoading, setHitlistsLoading] = useState(true);
  const [retailKadLoading, setRetailKadLoading] = useState(true);
  const [marketPremiumLoading, setMarketPremiumLoading] = useState(true);
  const [sellerInsightsLoading, setSellerInsightsLoading] = useState(true);

  const handleLogout = (e: StorageEvent) => logoutUser(e, isAuthenticated);

  async function fetchSubscriptionSection(options: FetchSectionOptions) {
    const {
      pageName,
      productType,
      productDataType,
      setSubscription,
      reduxState,
      updateRedux,
      setId,
      setCountries,
      setList,
      setLoadingFn,
    } = options;
    try {
      const sub = getSubscriptionWithProductType(subscriptions, productType);
      if (!isAuthorized(sub)) {
        setLoadingFn(false);
        return;
      }
      setSubscription(sub);
      addSubscribedProductType(productType);

      const info = validateSubscriptionAndGetRetailerInfo(
        productType,
        sub.result.restrictions,
        reduxState,
        updateRedux,
        pagesConfig,
        updateSideFilters,
        checkIsSameUser(user, pagesConfig, updateSideFilters),
      );
      setId(info.defaultRetailer);
      setCountries(info.retailerCountries);

      await getFiltersAndRetailerClients({
        pageName,
        productType,
        productDataType,
        retailerId: info.defaultRetailer,
        retailerCountries: info.retailerCountries,
        retailerList: info.retailerList,
        clientSubscription: sub,
        setRetailerClient: setList,
        setLoading: setLoadingFn,
        setHitlistsRetailerClient: () => {},
        setSellerInsightsRetailerClient: () => {},
        page,
        updateInitialFilters,
        updateInitialFiltersProducts,
        updateInitialFiltersMarketPremium,
      });

      await getAndStoreSavedFilters(productType, info.defaultRetailer, pagesConfig, user?.sub); //NOSONAR typescript:S1874
    } catch (err) {
      logSubscriptionError(err, productType, setLoadingFn);
    } finally {
      setLoadingFn(false);
    }
  }

  useEffect(() => {
    if (loadingSubscriptions) return;

    setLoading(true);
    setLocalStorage(StorageKey.UserData, user?.sub ?? ''); //NOSONAR typescript:S1874
    window.addEventListener('storage', handleLogout);
    Highcharts.AST.allowedAttributes.push('data-reactroot', 'data-test-id', 'data-testid');

    clearSubscribedProductType();
    pagesConfig.setIsMultiCountryFeatureFlagPA(true);
    pagesConfig.setIsMultiCountryDropdown(true);
    pagesConfig.setIsSideFiltersFeatureFlagEnabled(true);

    (async () => {
      await fetchSubscriptionSection({
        pageName: PageName.RETAILER_OVERVIEW,
        productType: ProductType.PerformanceAnalyzer,
        productDataType: ProductDataType.PerformanceAnalyzer,
        setSubscription: pagesConfig.setClientSubscription,
        reduxState: reduxStates.retailer,
        updateRedux: updateRetailerClient,
        setId: pagesConfig.setClientId,
        setCountries: pagesConfig.setClientCountries,
        setList: setRetailer,
        setLoadingFn: () => {},
      });

      await fetchSubscriptionSection({
        pageName: PageName.RI_PRODUCT,
        productType: ProductType.ProductAnalyzer,
        productDataType: ProductDataType.ProductAnalyzer,
        setSubscription: pagesConfig.setHitlistsClientSubscription,
        reduxState: reduxStates.hitlists,
        updateRedux: updateHitlistsRetailerClient,
        setId: pagesConfig.setHitlistsClientId,
        setCountries: pagesConfig.setHitlistsClientCountries,
        setList: setHitlists,
        setLoadingFn: setHitlistsLoading,
      });

      await fetchSubscriptionSection({
        pageName: PageName.RI_PRODUCT,
        productType: ProductType.SellerInsights,
        productDataType: ProductDataType.ProductAnalyzer,
        setSubscription: pagesConfig.setSellerInsightsClientSubscription,
        reduxState: reduxStates.seller,
        updateRedux: updateSellerInsightsRetailerClient,
        setId: pagesConfig.setSellerInsightsClientId,
        setCountries: pagesConfig.setSellerInsightsClientCountries,
        setList: setSeller,
        setLoadingFn: setSellerInsightsLoading,
      });

      await fetchSubscriptionSection({
        pageName: PageName.RI_PRODUCT,
        productType: ProductType.RetailKad,
        productDataType: ProductDataType.ProductAnalyzer,
        setSubscription: pagesConfig.setRetailKadClientSubscription,
        reduxState: reduxStates.retailer,
        updateRedux: updateRetailerClient,
        setId: pagesConfig.setClientId,
        setCountries: pagesConfig.setClientCountries,
        setList: setRetailKad,
        setLoadingFn: setRetailKadLoading,
      });

      await fetchSubscriptionSection({
        pageName: PageName.RETAILER_OVERVIEW,
        productType: ProductType.RetailKad,
        productDataType: ProductDataType.PerformanceAnalyzer,
        setSubscription: pagesConfig.setRetailKadClientSubscription,
        reduxState: reduxStates.retailer,
        updateRedux: updateRetailerClient,
        setId: pagesConfig.setClientId,
        setCountries: pagesConfig.setClientCountries,
        setList: setRetailKad,
        setLoadingFn: setRetailKadLoading,
      });

      await fetchSubscriptionSection({
        pageName: PageName.TRADING_ASSISTANT,
        productType: ProductType.MarketPremium,
        productDataType: ProductDataType.PerformanceAnalyzer,
        setSubscription: pagesConfig.setMarketPremiumClientSubscription,
        reduxState: reduxStates.market,
        updateRedux: updateMarketPremiumRetailerClient,
        setId: pagesConfig.setMarketPremiumClientId,
        setCountries: pagesConfig.setMarketPremiumClientCountries,
        setList: setMarket,
        setLoadingFn: setMarketPremiumLoading,
      });

      await fetchSubscriptionSection({
        pageName: PageName.TRADING_ASSISTANT,
        productType: ProductType.RetailKad,
        productDataType: ProductDataType.PerformanceAnalyzer,
        setSubscription: pagesConfig.setMarketPremiumClientSubscription,
        reduxState: reduxStates.market,
        updateRedux: updateMarketPremiumRetailerClient,
        setId: pagesConfig.setMarketPremiumClientId,
        setCountries: pagesConfig.setMarketPremiumClientCountries,
        setList: setMarket,
        setLoadingFn: setMarketPremiumLoading,
      });

      setLoading(false);
    })();

    return () => window.removeEventListener('storage', handleLogout);
  }, [loadingSubscriptions]);

  return {
    retailer,
    hitlists,
    retailKad,
    market,
    seller,
    hitlistsLoading,
    retailKadLoading,
    marketPremiumLoading,
    sellerInsightsLoading,
  };
}
